import { cn } from "@lib/cn";
import { useSlidesLibraryStoreShallow } from "@modules/slides-library/shared";
import { useState } from "react";
import type { TFolderWithChildren } from "@/shared/types";
import { IconChevronRight, IconFolder } from "@tabler/icons-react";
import { ArchivedFolderActions } from "./archived-folder-actions";

export const RecursiveArchivedFolder = ({
  folder,
  level = 0,
}: {
  folder: TFolderWithChildren;
  level?: number;
}) => {
  const { setFolderId, folderId } = useSlidesLibraryStoreShallow((s) => ({
    setFolderId: s.setFolderId,
    folderId: s.folderId,
  }));
  const isActive = folderId === folder._id;
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const paddingLeft = 24 + level * 16;
  const hasChildren = folder.children.length > 0;

  const handleRowClick = () => {
    setFolderId(folder._id);
  };

  return (
    <div>
      {/* biome-ignore lint/a11y/useSemanticElements: contains nested action buttons */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Open archived folder: ${folder.title}`}
        aria-current={isActive ? "true" : undefined}
        onClick={handleRowClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleRowClick();
          }
        }}
        className={cn(
          "tw-group/folder tw-relative tw-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-pr-1 tw-font-sans tw-text-[14px] tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-100 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200",
          isActive || isMenuOpen ? "tw-bg-blue-100" : "",
        )}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        <IconFolder
          className="tw-size-4 tw-shrink-0 tw-text-blue"
          stroke={1}
          aria-hidden="true"
        />
        <span className="tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
          {folder.title}
        </span>
        <div
          className={cn(
            "tw-invisible tw-flex tw-items-center tw-gap-0.5 group-hover/folder:tw-visible group-focus-within/folder:tw-visible",
            (isMenuOpen || isActive) && "tw-visible",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <ArchivedFolderActions
            isOpen={isMenuOpen}
            setIsOpen={setIsMenuOpen}
            folder={folder}
          />
          {hasChildren && (
            <button
              type="button"
              aria-label={isOpen ? `Collapse ${folder.title}` : `Expand ${folder.title}`}
              aria-expanded={isOpen}
              className="tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
            >
              <IconChevronRight
                className={cn(
                  "tw-size-3.5 tw-text-blue tw-opacity-50 tw-transition-transform",
                  isOpen && "tw-rotate-90",
                )}
                stroke={1.5}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>
      {hasChildren && (
        <div
          className={cn(
            "tw-grid tw-transition-[grid-template-rows] tw-duration-200",
            {
              "[grid-template-rows:1fr]": isOpen,
              "[grid-template-rows:0fr]": !isOpen,
            },
          )}
        >
          <div className="tw-overflow-hidden">
            <div className="tw-mt-0.5 tw-flex tw-flex-col tw-gap-0.5">
              {folder.children.map((child) => (
                <RecursiveArchivedFolder
                  key={child._id}
                  folder={child}
                  level={level + 1}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
